import { IDisposable, IEventAggregator, disposableSubscription, disposable } from 'common/barrels/core';
import { Map } from 'immutable';
import * as path from 'shared/path';
import { Serializer } from 'common/serializer';
import { blobFileName } from 'common/functions/imagenormalization';

import { FileUploadStartRequested } from 'runtime/events/fileuploadstartrequested';
import { FileUploadCancelRequested } from 'runtime/events/fileuploadcancelrequested';
import { FileUploadRegistered } from 'runtime/events/fileuploadregistered';
import { FileUploadUnregistered } from 'runtime/events/fileuploadunregistered';
import { FileUploadStarted } from 'runtime/events/fileuploadstarted';
import { FileUploadFinished } from 'runtime/events/fileuploadfinished';
import { FileUploadComplete } from 'runtime/events/fileuploadcomplete';
import { FileUploadVirusDetected } from 'runtime/events/fileuploadvirusdetected';
import { FileUploadBadFileFormat } from 'runtime/events/fileuploadbadfileformat';
import { FileUploadError } from 'runtime/events/fileuploaderror';
import { UploadResult } from 'runtime/data/uploadresult';
import { VirusDetected } from 'runtime/messages/virusdetected';
import { BadFileFormat } from 'runtime/messages/badfileformat';
import {FileParameter, Request} from "common/request";

export class UploadManager implements IDisposable {
    private subscriptions: IDisposable;
    private workItemId: string;
    private eventAggregator: IEventAggregator;
    private uploadMap = Map<string, IDisposable>();
    private serializer: Serializer;

    constructor(eventAggregator: IEventAggregator, workItemId: string, serializer: Serializer, csrfToken: () => string) {
        this.workItemId = workItemId;
        this.eventAggregator = eventAggregator;
        this.serializer = serializer;
        this.subscriptions = disposableSubscription(
            eventAggregator.query(FileUploadStartRequested).subscribe(e => this.startUpload(e, csrfToken)),
            eventAggregator.query(FileUploadCancelRequested).subscribe(e => this.cancelUpload(e)),
            eventAggregator.query(FileUploadRegistered).subscribe(e => this.registerFileAsUploading(e.transferId, null)),
            eventAggregator.query(FileUploadUnregistered).subscribe(e => this.unregisterFileAsUploading(e.transferId))
        );
    }

    dispose(): void {
        this.subscriptions.dispose();
    }

    private startUpload(event: FileUploadStartRequested, csrfToken: () => string) {
        const isBlob = event.source instanceof Blob;

        const uploadHandle = Request
            .create(this.serializer, path.ui('uploadfiles'))
            .args(() => ({
                workItemId: this.workItemId,
                imageOptions: event.imageOptions,
                fileNameOverride: event.fileNameOverride
            }))
            .handleArray(UploadResult, r => {
                const singleResult = r[0];
                this.eventAggregator.publish(new FileUploadComplete(event.transferId, singleResult.fileId, singleResult.fileName, singleResult.length));
                this.unregisterFileAsUploading(event.transferId);
            })
            .handle(VirusDetected, () => this.eventAggregator.publish(new FileUploadVirusDetected(event.transferId)))
            .handle(BadFileFormat, () => this.eventAggregator.publish(new FileUploadBadFileFormat(event.transferId)))
            .unknown(() => this.eventAggregator.publish(new FileUploadError(event.transferId)))
            .error(() => this.eventAggregator.publish(new FileUploadError(event.transferId)))
            .post(csrfToken(), undefined, new FileParameter('upload', isBlob ? event.source : event.source.files[0], isBlob ? blobFileName((event.source as Blob).type) : undefined));

        this.registerFileAsUploading(event.transferId, uploadHandle);
    }

    private cancelUpload(event: FileUploadCancelRequested) {
        var cancellationHandle = this.uploadMap.get(event.transferId);
        if (!!cancellationHandle)
            cancellationHandle.dispose();
        this.unregisterFileAsUploading(event.transferId);
    }

    private registerFileAsUploading(transferId: string, uploadHandle) {
        if (this.uploadMap.has(transferId))
            return;

        this.uploadMap = this.uploadMap.set(transferId, uploadHandle);

        if (this.uploadMap.count() === 1)
            this.eventAggregator.publish(new FileUploadStarted());
    }

    private unregisterFileAsUploading(transferId: string) {
        if (!this.uploadMap.has(transferId))
            return;

        this.uploadMap = this.uploadMap.remove(transferId);

        if (this.uploadMap.count() === 0)
            this.eventAggregator.publish(new FileUploadFinished());
    }
}